import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { styled, keyframes } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';

// Мерцание
const twinkle = keyframes`
  0% { opacity: 0.3; }
  50% { opacity: 1; }
  100% { opacity: 0.3; }
`;

// Эффект туманности
export const NebulaEffect = styled(Box)(() => ({
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: `
        radial-gradient(ellipse at 20% 50%, rgba(59, 190, 219, 0.2) 0%, transparent 50%),
        radial-gradient(ellipse at 80% 20%, rgba(34, 211, 238, 0.15) 0%, transparent 50%),
        radial-gradient(ellipse at 40% 80%, rgba(6, 182, 212, 0.25) 0%, transparent 50%)
    `,
    animation: 'nebulaDrift 30s ease-in-out infinite',
    zIndex: 2,
    '@keyframes nebulaDrift': {
        '0%, 100%': {
            transform: 'translateX(0) translateY(0) rotate(0deg)',
            opacity: 0.7,
        },
        '25%': {
            transform: 'translateX(-20px) translateY(-10px) rotate(1deg)',
            opacity: 0.8,
        },
        '50%': {
            transform: 'translateX(20px) translateY(20px) rotate(-1deg)',
            opacity: 0.6,
        },
        '75%': {
            transform: 'translateX(10px) translateY(-20px) rotate(0.5deg)',
            opacity: 0.9,
        },
    },
}));

// Функция создаёт строку с radial-gradient
function generateStars(count: number, size: number) {
    const stars: string[] = [];
    for (let i = 0; i < count; i++) {
        const x = Math.random() * 100; // %
        const y = Math.random() * 100; // %
        stars.push(`radial-gradient(${size}px ${size}px at ${x}% ${y}%, white, transparent)`);
    }
    return stars.join(',');
}

export const StarryBackground = styled('div')(() => {
    const smallStars = generateStars(80, 1);
    const mediumStars = generateStars(40, 2);

    return {
        position: 'relative',
        height: '100vh',
        width: '100vw',
        // background: 'linear-gradient(135deg, #0f172a 0%, #1e3a8a 50%, #3730a3 100%)',
        background: 'linear-gradient(110deg, rgba(59, 190, 219, 1) 20%, rgba(238, 234, 252, 1) 112%, rgba(227, 247, 250, 1) 100%)',
        overflow: 'hidden',

        '&::before, &::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundRepeat: 'no-repeat',
            pointerEvents: 'none',
        },

        '&::before': {
            backgroundImage: smallStars,
            animation: `${twinkle} 6s linear infinite`,
        },

        '&::after': {
            backgroundImage: mediumStars,
            animation: `${twinkle} 10s linear infinite alternate`,
        },
    };
});

// Контейнер для меню модулей с ограничениями
export const MenuContainer = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '24px',
    gap: '24px',
    height: '100vh',
    overflow: 'hidden',
}));

// Заголовок и кнопка выхода в одной строке
export const HeaderContainer = styled(Box)(() => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
}));

// Заголовок с логотипом
export const TitleContainer = styled(Box)(() => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
}));

// Кнопка выхода
export const ExitButton = styled(Typography)(() => ({
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 600,
    color: 'white',
    transition: 'all 0.3s ease',
    '&:hover': {
        color: 'rgba(6, 182, 212, 0.8)',
        textShadow: '0 0 10px rgba(6, 182, 212, 0.5)',
    },
}));

// Пустой элемент для балансировки
export const Spacer = styled(Box)(() => ({
    width: '100px', // такая же ширина как у кнопки выхода
}));

// Подзаголовок
export const SubtitleContainer = styled(Box)(() => ({
    width: '100%',
    textAlign: 'center',
}));

export const StyledGrid = styled('div')`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    width: 100%;
    padding: 16px;

    @media (max-width: 900px) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 600px) {
        grid-template-columns: repeat(1, 1fr);
    }
`;

export const StyledButton = styled(IconButton)`
    width: 100%;
    padding: 20px;
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(6, 182, 212, 0.2) 100%);
    backdrop-filter: blur(16px);
    border: 1px solid rgba(59, 130, 246, 0.3);
    border-radius: 12px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    color: white;
    text-transform: none;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;

    &:hover {
        transform: translateY(-2px) scale(1.02);
        background: linear-gradient(135deg, rgba(59, 130, 246, 0.4) 0%, rgba(6, 182, 212, 0.4) 100%);
        border-color: rgba(6, 182, 212, 0.8);
        box-shadow: 0 8px 25px rgba(59, 130, 246, 0.4);
    }

    &:active {
        transform: translateY(0) scale(1);
    }
`;